import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useTranslateWordMutation,
  useSaveVocabularyMemoryMutation,
} from '@/store/api/user/vocabularyApi';
import TranslationPopover from './TranslationPopover';

// ---------------------------------------------------------------------------
// Scope rules (UI-level — backend does not enforce this).
// Highlight-to-translate is ENABLED inside exercises, assignments, practice
// sessions and review screens. It is DISABLED inside mock exams / real exam
// simulation.
// ---------------------------------------------------------------------------

// Route patterns → source mapping for the memory entry.
// type ∈ exercise | assignment | practice | review
const ROUTE_RULES = [
  {
    test: (p) => /^\/exercise\//.test(p),
    build: (p) => {
      const id = p.split('/')[2];
      return { type: 'exercise', id, label: 'Exercise' };
    },
  },
  {
    test: (p) => /^\/my-exercises/.test(p),
    build: () => ({ type: 'exercise', label: 'Exercises' }),
  },
  {
    test: (p) => /^\/trial-laboratory\/test\//.test(p),
    build: (p) => {
      const idx = parseInt(p.split('/')[3], 10);
      const n = Number.isFinite(idx) ? idx + 1 : null;
      return {
        type: 'practice',
        label: n ? `Trial Lab Test ${n}` : 'Trial Laboratory',
      };
    },
  },
  {
    test: (p) => /^\/trial-laboratory/.test(p),
    build: () => ({ type: 'practice', label: 'Trial Laboratory' }),
  },
  {
    test: (p) => /^\/statistics/.test(p),
    build: () => ({ type: 'statistics', label: 'Statistics' }),
  },
  {
    test: (p) => /^\/review/.test(p),
    build: () => ({ type: 'review', label: 'Review Answers' }),
  },
];

// Explicitly disallowed routes (mock exams / real simulation).
const BLOCKED_PATTERNS = [/^\/exam-session\//];

const resolveScope = (pathname) => {
  if (BLOCKED_PATTERNS.some((re) => re.test(pathname))) {
    return { enabled: false, source: null };
  }
  for (const rule of ROUTE_RULES) {
    if (rule.test(pathname)) {
      return { enabled: true, source: rule.build(pathname) };
    }
  }
  return { enabled: false, source: null };
};

// ---------------------------------------------------------------------------
// Context: allows nested components to temporarily override source/enabled.
// ---------------------------------------------------------------------------
const VocabularyLookupContext = createContext({
  enabled: false,
  source: null,
  setSourceOverride: () => {},
});

export const useVocabularyLookup = () => useContext(VocabularyLookupContext);

/**
 * Optional helper for pages to override the default source label/id
 * (e.g. an exercise page that knows the specific Exercise Day).
 */
export const VocabularyLookupScope = ({ source, children }) => {
  const parent = useContext(VocabularyLookupContext);
  const [override, setOverride] = useState(source || null);

  useEffect(() => {
    setOverride(source || null);
  }, [source]);

  const value = useMemo(
    () => ({
      enabled: parent.enabled,
      source: override || parent.source,
      setSourceOverride: setOverride,
    }),
    [parent.enabled, parent.source, override]
  );

  return (
    <VocabularyLookupContext.Provider value={value}>
      {children}
    </VocabularyLookupContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------
const MAX_WORD_LENGTH = 100;
const MAX_SENTENCE_LENGTH = 500;

// Accept 1–6 English word-like tokens. Letters, hyphens, apostrophes allowed.
const WORD_PATTERN = /^[A-Za-z][A-Za-z'\-]*(?:\s+[A-Za-z][A-Za-z'\-]*){0,5}$/;

const cleanSelection = (raw) => {
  if (!raw) return null;
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_WORD_LENGTH) return null;
  if (!WORD_PATTERN.test(trimmed)) return null;
  return trimmed.toLowerCase();
};

// Extract the full sentence containing the selection from the parent element's
// text content. Walks to the nearest sentence boundary (. ? !) in both
// directions, falling back to the full parent text. Result is capped at
// MAX_SENTENCE_LENGTH to respect the backend contract.
const extractSentence = (range) => {
  try {
    const container = range.startContainer;
    const node = container.nodeType === 3 ? container.parentElement : container;
    if (!node) return '';
    const parentText = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (!parentText) return '';

    const selected = range.toString().trim();
    if (!selected) return parentText.slice(0, MAX_SENTENCE_LENGTH);

    const lower = parentText.toLowerCase();
    const idx = lower.indexOf(selected.toLowerCase());
    if (idx === -1) return parentText.slice(0, MAX_SENTENCE_LENGTH);

    // Walk backward to the last sentence boundary before the selection
    const before = parentText.slice(0, idx);
    const backMatches = [...before.matchAll(/[.?!]\s+/g)];
    const startIdx = backMatches.length
      ? backMatches[backMatches.length - 1].index +
        backMatches[backMatches.length - 1][0].length
      : 0;

    // Walk forward to the next sentence boundary after the selection
    const after = parentText.slice(idx + selected.length);
    const fwdMatch = after.match(/[.?!](\s|$)/);
    const endIdx = fwdMatch
      ? idx + selected.length + fwdMatch.index + 1
      : parentText.length;

    let sentence = parentText.slice(startIdx, endIdx).trim();
    if (sentence.length > MAX_SENTENCE_LENGTH) {
      const selIdx = sentence.toLowerCase().indexOf(selected.toLowerCase());
      const s = Math.max(0, (selIdx === -1 ? 0 : selIdx) - 200);
      const e = Math.min(sentence.length, s + MAX_SENTENCE_LENGTH);
      sentence = sentence.slice(s, e);
    }
    return sentence;
  } catch {
    return '';
  }
};

// Avoid triggering while the user is interacting with form controls.
const isInEditable = (node) => {
  if (!node) return false;
  const el = node.nodeType === 3 ? node.parentElement : node;
  if (!el) return false;
  if (el.closest('input, textarea, [contenteditable="true"]')) return true;
  if (el.closest('[data-vocab-lookup-popover="true"]')) return true;
  // Skip selection inside the popover itself
  return false;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
const VocabularyLookupProvider = ({ children }) => {
  const location = useLocation();
  const { enabled, source: routeSource } = useMemo(
    () => resolveScope(location.pathname || '/'),
    [location.pathname]
  );

  const [sourceOverride, setSourceOverride] = useState(null);
  const source = sourceOverride || routeSource;

  const [anchorEl, setAnchorEl] = useState(null);
  const [word, setWord] = useState('');
  const [sentence, setSentence] = useState('');
  const [wordTranslation, setWordTranslation] = useState('');
  const [contextMeaning, setContextMeaning] = useState('');
  const [sentenceTranslation, setSentenceTranslation] = useState('');
  const [pronunciation, setPronunciation] = useState(null);
  const [saved, setSaved] = useState(false);
  const [memoryId, setMemoryId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestSeq = useRef(0);
  // Key of the last successful lookup (word + sentence). Used to skip
  // redundant re-fetches when the same selection is "re-detected" after
  // interacting with the popover (e.g. clicking the pronounce button).
  const lastLookupKeyRef = useRef(null);

  // Primary translation shown in the tooltip: prefer the context-resolved
  // meaning, fall back to the dictionary-style word translation.
  const primaryTranslation = wordTranslation;

  const [translateWord] = useTranslateWordMutation();
  const [saveMemory, { isLoading: isSaving }] = useSaveVocabularyMemoryMutation();

  const close = useCallback(() => {
    setAnchorEl(null);
    setWord('');
    setSentence('');
    setWordTranslation('');
    setContextMeaning('');
    setSentenceTranslation('');
    setPronunciation(null);
    setSaved(false);
    setError(null);
    setLoading(false);
    lastLookupKeyRef.current = null;
  }, []);

  // Reset override on route change so stale data never leaks across routes.
  useEffect(() => {
    setSourceOverride(null);
    close();
  }, [location.pathname, close]);

  // Dismiss when clicking outside the popover.
  useEffect(() => {
    if (!anchorEl) return undefined;
    const handleDocDown = (e) => {
      if (e.target?.closest?.('[data-vocab-lookup-popover="true"]')) return;
      // If a new selection triggers handleSelection, we'll re-open anyway.
      close();
    };
    document.addEventListener('mousedown', handleDocDown);
    return () => document.removeEventListener('mousedown', handleDocDown);
  }, [anchorEl, close]);

  // Close on Escape
  useEffect(() => {
    if (!anchorEl) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [anchorEl, close]);

  const performLookup = useCallback(
    async (cleaned, rect, sentenceStr) => {
      const seq = ++requestSeq.current;

      // Virtual anchor based on selection bounding rect
      const virtualAnchor = {
        nodeType: 1,
        getBoundingClientRect: () => rect,
        // eslint-disable-next-line no-undef
        ownerDocument: document,
      };

      setAnchorEl(virtualAnchor);
      setWord(cleaned);
      setSentence(sentenceStr || '');
      setWordTranslation('');
      setContextMeaning('');
      setSentenceTranslation('');
      setPronunciation(null);
      setSaved(false);
      setMemoryId(null);
      setError(null);
      setLoading(true);

      try {
        const body = { word: cleaned };
        if (sentenceStr && sentenceStr.length <= MAX_SENTENCE_LENGTH) {
          body.sentence = sentenceStr;
        }
        const res = await translateWord(body).unwrap();
        if (seq !== requestSeq.current) return; // stale
        const data = res?.data || {};
        setWordTranslation(data.wordTranslation || data.translation || '');
        setContextMeaning(data.contextMeaning || '');
        setSentenceTranslation(data.sentenceTranslation || '');
        setPronunciation(data.pronunciation || null);
        setSaved(!!data.saved);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        const status = err?.status;
        setError(
          status === 503
            ? 'Translation service is temporarily unavailable.'
            : err?.data?.message || 'Could not translate this word.'
        );
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [translateWord]
  );

  // Global selection listener
  useEffect(() => {
    if (!enabled) return undefined;

    const handleSelection = (event) => {
      // Ignore events that originate from the popover itself (e.g. the
      // pronounce button) — they must never re-trigger a lookup.
      const evTarget = event?.target;
      if (
        evTarget &&
        evTarget.closest &&
        evTarget.closest('[data-vocab-lookup-popover="true"]')
      ) {
        return;
      }

      // Give the browser a tick to finalize the selection (touch devices).
      setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        const startNode = range.startContainer;
        const endNode = range.endContainer;
        if (isInEditable(startNode) || isInEditable(endNode)) return;

        const raw = selection.toString();
        const cleaned = cleanSelection(raw);
        if (!cleaned) return;

        const rect = range.getBoundingClientRect();
        if (!rect || (rect.width === 0 && rect.height === 0)) return;

        const sent = extractSentence(range);

        // Skip redundant lookups for the exact same word+sentence pair —
        // this avoids re-fetching when the user just interacts with the
        // popover while the previous selection is still alive.
        const key = `${cleaned}||${sent || ''}`;
        if (lastLookupKeyRef.current === key) return;
        lastLookupKeyRef.current = key;

        performLookup(cleaned, rect, sent);
      }, 0);
    };

    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('touchend', handleSelection);
    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('touchend', handleSelection);
    };
  }, [enabled, performLookup]);

  // Also close when leaving a scope that is no longer enabled.
  useEffect(() => {
    if (!enabled) close();
  }, [enabled, close]);

  const handleSave = useCallback(async () => {
    if (!word || !primaryTranslation) return;
    try {
      const body = {
        word,
        translation: primaryTranslation,
        context: sentence || undefined,
      };
      if (source?.type) {
        body.source = {
          type: source.type,
          ...(source.id ? { id: source.id } : {}),
          ...(source.label ? { label: source.label } : {}),
        };
      }
      const res = await saveMemory(body).unwrap();
      setSaved(true);
      setMemoryId(res?.data?._id || null);
      toast.success('Saved to Memories');
    } catch (err) {
      toast.error(err?.data?.message || 'Could not save the word');
    }
  }, [word, primaryTranslation, sentence, source, saveMemory]);

  const ctxValue = useMemo(
    () => ({ enabled, source, setSourceOverride }),
    [enabled, source]
  );

  return (
    <VocabularyLookupContext.Provider value={ctxValue}>
      {children}
      {enabled && (
        <TranslationPopover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          word={word}
          translation={primaryTranslation}
          wordTranslation={wordTranslation}
          contextMeaning={contextMeaning}
          sentenceTranslation={sentenceTranslation}
          pronunciation={pronunciation}
          saved={saved}
          loading={loading}
          saving={isSaving}
          error={error}
          onSave={handleSave}
          onClose={close}
        />
      )}
    </VocabularyLookupContext.Provider>
  );
};

export default VocabularyLookupProvider;
